import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccountBalance as AccountIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Person as PersonIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import { getOwnedAccounts, getOwnedAccountAssignments } from '../../services/jitApi';

const JITOwnedAccounts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAccountId, setExpandedAccountId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountsRes, assignmentsRes] = await Promise.all([
        getOwnedAccounts(),
        getOwnedAccountAssignments(),
      ]);
      setAccounts(accountsRes.data || []);
      setAssignments(assignmentsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch owned accounts:', err);
      setError('Failed to load your owned accounts');
    } finally {
      setLoading(false);
    }
  };

  // Group assignments by account ID
  const assignmentsByAccount = useMemo(() => {
    const map = {};
    assignments.forEach(a => {
      const accountId = a.accountId || a.account_id;
      if (!map[accountId]) map[accountId] = [];
      map[accountId].push(a);
    });
    return map;
  }, [assignments]);

  // Filter accounts based on search query
  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return accounts;

    const query = searchQuery.toLowerCase();
    return accounts.filter(account => {
      const name = (account.name || '').toLowerCase();
      const accountId = (account.account_id || account.accountId || '').toLowerCase();
      const owners = (account.owner_emails || account.ownerEmails || []).join(' ').toLowerCase();
      return name.includes(query) || accountId.includes(query) || owners.includes(query);
    });
  }, [accounts, searchQuery]);

  const handleRowClick = (accountId) => {
    setExpandedAccountId(prev => prev === accountId ? null : accountId);
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return '-';
    try {
      return new Date(expiresAt).toLocaleString();
    } catch {
      return expiresAt;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <motion.div variants={sectionVariants}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            Owned Accounts
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            AWS accounts you own — click an account to see who has access
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={fetchData}
            sx={{
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#4698D3',
                backgroundColor: 'rgba(70, 152, 211, 0.04)',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      </motion.div>

      {/* Search Bar */}
      {accounts.length > 0 && (
        <TextField
          fullWidth
          placeholder="Search by account name, ID, or owner..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: '#FFFFFF',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4698D3',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#999999' }} />
              </InputAdornment>
            ),
          }}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <motion.div variants={sectionVariants}>
      {filteredAccounts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(70, 152, 211, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <AccountIcon sx={{ fontSize: 32, color: '#4698D3' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            {searchQuery ? 'No Matching Accounts' : 'No Owned Accounts'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            {searchQuery
              ? 'No accounts match your search criteria'
              : 'You are not listed as an owner for any AWS accounts'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            overflowX: 'auto',
          }}
        >
          <Table sx={{ minWidth: { xs: 400, sm: 600 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, width: 40 }} />
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Account
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Account ID
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Owners
                </TableCell>
                <TableCell align="center" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Pending
                </TableCell>
                <TableCell align="center" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Active
                </TableCell>
                <TableCell align="center" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Assignments
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAccounts.map((account, index) => {
                const ownerEmails = account.owner_emails || account.ownerEmails || [];
                const accountId = account.account_id || account.accountId;
                const isExpanded = expandedAccountId === accountId;
                const accountAssignments = assignmentsByAccount[accountId] || [];

                return (
                  <React.Fragment key={accountId || account.id}>
                    <TableRow
                      hover
                      onClick={() => handleRowClick(accountId)}
                      sx={{
                        cursor: 'pointer',
                        '@keyframes fadeInRow': {
                          from: { opacity: 0, transform: 'translateY(8px)' },
                          to: { opacity: 1, transform: 'translateY(0)' },
                        },
                        animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                        '&:hover': {
                          backgroundColor: 'rgba(70, 152, 211, 0.02)',
                        },
                        ...(isExpanded && {
                          backgroundColor: 'rgba(70, 152, 211, 0.04)',
                        }),
                      }}
                    >
                      <TableCell sx={{ width: 40, pr: 0 }}>
                        <IconButton size="small">
                          {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {!isMobile && (
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '6px',
                                backgroundColor: 'rgba(70, 152, 211, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <AccountIcon sx={{ color: '#4698D3', fontSize: 18 }} />
                            </Box>
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              color: '#253E66',
                            }}
                          >
                            {account.name || accountId || 'Unnamed Account'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: '#666666',
                            fontSize: '13px',
                          }}
                        >
                          {accountId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {ownerEmails.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {ownerEmails.slice(0, 2).map((email) => (
                              <Chip
                                key={email}
                                size="small"
                                label={email.split('@')[0]}
                                sx={{
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontSize: '0.7rem',
                                  height: 22,
                                }}
                              />
                            ))}
                            {ownerEmails.length > 2 && (
                              <Tooltip title={ownerEmails.slice(2).join(', ')}>
                                <Chip
                                  size="small"
                                  label={`+${ownerEmails.length - 2}`}
                                  sx={{
                                    fontFamily: 'Metropolis, sans-serif',
                                    fontSize: '0.7rem',
                                    height: 22,
                                    backgroundColor: 'rgba(70, 152, 211, 0.1)',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#999999',
                              fontFamily: 'Metropolis, sans-serif',
                              fontStyle: 'italic',
                            }}
                          >
                            No owners
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={account.pending_requests || account.pendingRequests || 0}
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            minWidth: 32,
                            backgroundColor: (account.pending_requests || account.pendingRequests || 0) > 0
                              ? 'rgba(243, 156, 18, 0.15)'
                              : 'rgba(0, 0, 0, 0.04)',
                            color: (account.pending_requests || account.pendingRequests || 0) > 0
                              ? '#F39C12'
                              : '#666666',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={account.active_sessions || account.activeSessions || 0}
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            minWidth: 32,
                            backgroundColor: (account.active_sessions || account.activeSessions || 0) > 0
                              ? 'rgba(39, 174, 96, 0.15)'
                              : 'rgba(0, 0, 0, 0.04)',
                            color: (account.active_sessions || account.activeSessions || 0) > 0
                              ? '#27AE60'
                              : '#666666',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={accountAssignments.length}
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            minWidth: 32,
                            backgroundColor: accountAssignments.length > 0
                              ? 'rgba(70, 152, 211, 0.12)'
                              : 'rgba(0, 0, 0, 0.04)',
                            color: accountAssignments.length > 0
                              ? '#4698D3'
                              : '#666666',
                          }}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Expandable assignment detail */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 3, py: 2, backgroundColor: '#F9FBFD' }}>
                            {accountAssignments.length === 0 ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#999999',
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontStyle: 'italic',
                                  textAlign: 'center',
                                  py: 2,
                                }}
                              >
                                No one currently has access to this account
                              </Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.8rem' }}>
                                      User / Group
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.8rem' }}>
                                      Permission Set
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.8rem' }}>
                                      Access Type
                                    </TableCell>
                                    {!isMobile && (
                                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.8rem' }}>
                                        Expires At
                                      </TableCell>
                                    )}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {accountAssignments.map((assignment) => {
                                    const isJit = assignment.isJit || assignment.is_jit;
                                    const principalType = assignment.principalType || assignment.principal_type;
                                    const principalName = assignment.principalName || assignment.principal_name || '-';
                                    const psName = assignment.permissionSetName || assignment.permission_set_name || '';
                                    const expiresAt = assignment.jitExpiresAt || assignment.jit_expires_at;

                                    return (
                                      <TableRow key={assignment.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {principalType === 'GROUP' ? (
                                              <GroupIcon sx={{ color: '#8E44AD', fontSize: 16 }} />
                                            ) : (
                                              <PersonIcon sx={{ color: '#4698D3', fontSize: 16 }} />
                                            )}
                                            <Box>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontFamily: 'Metropolis, sans-serif',
                                                  fontWeight: 500,
                                                  color: '#253E66',
                                                  fontSize: '0.82rem',
                                                }}
                                              >
                                                {principalName}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: '#999999',
                                                  fontFamily: 'Metropolis, sans-serif',
                                                  fontSize: '0.7rem',
                                                }}
                                              >
                                                {principalType === 'GROUP' ? 'Group' : 'User'}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontFamily: 'Metropolis, sans-serif',
                                              color: '#333333',
                                              fontSize: '0.82rem',
                                            }}
                                          >
                                            {psName}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Chip
                                            size="small"
                                            label={isJit ? 'JIT' : 'Permanent'}
                                            sx={{
                                              fontFamily: 'Metropolis, sans-serif',
                                              fontWeight: 600,
                                              fontSize: '0.68rem',
                                              height: 22,
                                              backgroundColor: isJit
                                                ? 'rgba(243, 156, 18, 0.12)'
                                                : 'rgba(70, 152, 211, 0.12)',
                                              color: isJit ? '#F39C12' : '#4698D3',
                                            }}
                                          />
                                        </TableCell>
                                        {!isMobile && (
                                          <TableCell>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontFamily: 'Metropolis, sans-serif',
                                                color: isJit ? '#F39C12' : '#999999',
                                                fontStyle: isJit ? 'normal' : 'italic',
                                                fontSize: '0.8rem',
                                              }}
                                            >
                                              {isJit ? formatExpiry(expiresAt) : 'Never'}
                                            </Typography>
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </motion.div>
    </motion.div>
  );
};

export default JITOwnedAccounts;
